import { useState, useEffect } from 'react'
import {
  Bot,
  Plus,
  Trash2,
  Edit,
  ChevronRight,
  ChevronDown,
  AlertCircle,
  Save,
  X,
  Sparkles,
  Settings,
  Search,
  CheckSquare,
  Square,
  Eye,
  FileText,
  Maximize2,
  Minimize2,
} from 'lucide-react'
import {
  AI_PROVIDERS,
  PROVIDER_STYLES,
  REASONING_STYLES,
  getProviderFromModel,
  getModelInfo,
  getProviderInfo,
  getReasoningLevelsForProvider,
  getDefaultReasoningForProvider,
} from '@/config/models'
import {
  getAgents,
  createAgent,
  updateAgent,
  deleteAgent,
  createDefaultAgents,
  getKBDocuments,
  getKBDocumentContent,
  getGlobalAgentConfig,
  updateGlobalAgentConfig,
  type CustomAgent,
  type AgentCategory,
  type KBDocument,
  type GlobalAgentConfig,
  type ResponseTemplate,
} from '@/services/api'
import { useAuth } from '@/hooks/useAuth'


// Module-level cache to persist data across navigation
let _cachedAgents: CustomAgent[] | null = null
let _cachedCategories: AgentCategory[] | null = null

export default function AgentsPage() {
  const { isSuperAdmin } = useAuth()
  const [agents, setAgents] = useState<CustomAgent[]>(_cachedAgents || [])
  const [categories, setCategories] = useState<AgentCategory[]>(_cachedCategories || [])
  const [loading, setLoading] = useState(!_cachedAgents)
  const [error, setError] = useState<string | null>(null)
  const [selectedAgent, setSelectedAgent] = useState<CustomAgent | null>(null)
  const [isEditing, setIsEditing] = useState(false)
  const [isCreating, setIsCreating] = useState(false)
  const [saving, setSaving] = useState(false)
  const [activeTab, setActiveTab] = useState<'configuration' | 'training'>('configuration')
  const [kbDocuments, setKbDocuments] = useState<KBDocument[]>([])
  const [kbLoading, setKbLoading] = useState(false)
  const [kbLoaded, setKbLoaded] = useState(false)
  const [kbError, setKbError] = useState<string | null>(null)
  const [kbSearchQuery, setKbSearchQuery] = useState('')
  const [previewDocId, setPreviewDocId] = useState<string | null>(null)
  const [previewContent, setPreviewContent] = useState<string | null>(null)
  const [previewLoading, setPreviewLoading] = useState(false)

  // Global config state
  const [globalConfig, setGlobalConfig] = useState<GlobalAgentConfig>({})
  const [isEditingGlobal, setIsEditingGlobal] = useState(false)
  const [globalFormData, setGlobalFormData] = useState<GlobalAgentConfig>({})
  const [savingGlobal, setSavingGlobal] = useState(false)
  const [showGlobalSettings, setShowGlobalSettings] = useState(false)
  const [expandedScenarios, setExpandedScenarios] = useState<Record<string, boolean>>({})
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    model_config: true,
    common_tone: true,
    escalation_protocol: false,
    compensation_limits: false,
    corporate_guardrails: false,
    store_data: false,
    context_instructions: false,
    special_instructions: false,
  })

  // Form state
  const [formData, setFormData] = useState<Partial<CustomAgent>>({})

  // Load agents and global config
  useEffect(() => {
    loadAgents()
    loadGlobalConfig()
  }, [])

  const loadAgents = async () => {
    try {
      if (!_cachedAgents) setLoading(true)
      setError(null)
      const response = await getAgents()
      const newAgents = response.agents || []
      const newCategories = response.categories || []
      setAgents(newAgents)
      setCategories(newCategories)
      _cachedAgents = newAgents
      _cachedCategories = newCategories
    } catch (err: any) {
      setError(err.message || 'Failed to load agents')
      if (!_cachedAgents) setAgents([])
    } finally {
      setLoading(false)
    }
  }

  const loadGlobalConfig = async () => {
    try {
      const config = await getGlobalAgentConfig()
      setGlobalConfig(config)
      setGlobalFormData(config)
    } catch (err: any) {
      console.error('Failed to load global config:', err)
      // Initialize with empty config
      setGlobalConfig({})
      setGlobalFormData({})
    }
  }

  const handleEditGlobal = () => {
    setGlobalFormData({ ...globalConfig })
    setIsEditingGlobal(true)
  }

  const handleCancelGlobal = () => {
    setGlobalFormData({ ...globalConfig })
    setIsEditingGlobal(false)
  }

  const handleSaveGlobal = async () => {
    try {
      setSavingGlobal(true)
      setError(null)
      const response = await updateGlobalAgentConfig(globalFormData)
      setGlobalConfig(response.config)
      setGlobalFormData(response.config)
      setIsEditingGlobal(false)
    } catch (err: any) {
      setError(err.message || 'Failed to save global configuration')
    } finally {
      setSavingGlobal(false)
    }
  }

  const toggleSection = (section: string) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }))
  }

  const expandAllSections = () => {
    setExpandedSections({
      common_tone: true,
      escalation_protocol: true,
      compensation_limits: true,
      corporate_guardrails: true,
      store_data: true,
      context_instructions: true,
      special_instructions: true,
    })
  }

  const collapseAllSections = () => {
    setExpandedSections({
      common_tone: false,
      escalation_protocol: false,
      compensation_limits: false,
      corporate_guardrails: false,
      store_data: false,
      context_instructions: false,
      special_instructions: false,
    })
  }

  const handleCreateDefaults = async () => {
    try {
      setSaving(true)
      await createDefaultAgents('Company')
      await loadAgents()
    } catch (err: any) {
      setError(err.message || 'Failed to create default agents')
    } finally {
      setSaving(false)
    }
  }

  const handleCreateNew = () => {
    setFormData({
      name: '',
      description: '',
      categories: [],
      is_default: false,
      router_enabled: true,
      draft_response_enabled: true,
      response_mode: 'auto_reply',
      response_templates: [],
      agent_prompt: `DIN ROLLE:
Du er [agentens navn], ansvarlig for [ansvarsområde]. Du håndterer henvendelser knyttet til [kategori, f.eks. ordre, retur, frakt].

OPPFØRSEL:
- Svar alltid høflig, konsist og løsningsorientert.
- Bruk kundens navn når det er tilgjengelig.
- Ikke del intern informasjon eller spekuler utover det du vet.
- Eskaler til menneske hvis du er usikker eller saken er sensitiv.

VANLIGE SITUASJONER:
- Situasjon 1: Kunden spør om leveringstid → Oppgi estimert leveringstid basert på tilgjengelig info.
- Situasjon 2: Kunden ønsker retur → Forklar returprosessen steg for steg.
- Situasjon 3: Kunden klager på produkt → Vis empati, be om detaljer, og tilby løsning.

SVARLENGDE: 2-4 setninger per svar.`,
      knowledge_base_file_ids: [],
      tone: formData.tone || undefined,
      rules: [],
      temperature: 0.3,
      max_tokens: 500,
      default_language: 'NO',
      supported_languages: ['NO', 'EN', 'DA', 'SV'],
      signature_name: 'Kundeservice',
      signature_company: 'Snatched',
      escalation_enabled: true,
      max_auto_compensation: 500,
      max_auto_discount_percent: 10,
      enabled_tools: [],
    })
    setIsCreating(true)
    setSelectedAgent(null)
    setIsEditing(true)
    setActiveTab('configuration')
  }

  const handleSelectAgent = (agent: CustomAgent) => {
    setSelectedAgent(agent)
    setFormData({ ...agent })
    setIsCreating(false)
    setIsEditing(false)
    setActiveTab('configuration')
  }

  const handleEdit = () => {
    if (selectedAgent) {
      setFormData({ ...selectedAgent })
      setIsEditing(true)
    }
  }

  const handleCancel = () => {
    setIsEditing(false)
    setIsCreating(false)
    if (selectedAgent) {
      setFormData({ ...selectedAgent })
    }
  }

  const handleSave = async () => {
    try {
      setSaving(true)
      if (isCreating) {
        const response = await createAgent(formData)
        setAgents([...agents, response.agent])
        setSelectedAgent(response.agent)
      } else if (selectedAgent) {
        const response = await updateAgent(selectedAgent.agent_id, formData)
        setAgents(agents.map(a => a.agent_id === selectedAgent.agent_id ? response.agent : a))
        setSelectedAgent(response.agent)
      }
      setIsEditing(false)
      setIsCreating(false)
    } catch (err: any) {
      setError(err.message || 'Failed to save agent')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!selectedAgent) return
    if (!confirm(`Are you sure you want to delete "${selectedAgent.name}"?`)) return

    try {
      setSaving(true)
      await deleteAgent(selectedAgent.agent_id)
      setAgents(agents.filter(a => a.agent_id !== selectedAgent.agent_id))
      setSelectedAgent(null)
      setIsEditing(false)
    } catch (err: any) {
      setError(err.message || 'Failed to delete agent')
    } finally {
      setSaving(false)
    }
  }

  const loadKBDocuments = async () => {
    try {
      setKbLoading(true)
      setKbError(null)
      const response = await getKBDocuments()
      setKbDocuments(response.documents || [])
    } catch (err: any) {
      setKbError(err.message || 'Failed to load knowledge base documents')
    } finally {
      setKbLoading(false)
      setKbLoaded(true)
    }
  }

  const handleToggleKnowledgeBaseDoc = (documentId: string) => {
    const currentIds = formData.knowledge_base_file_ids || []
    const nextIds = currentIds.includes(documentId)
      ? currentIds.filter(id => id !== documentId)
      : [...currentIds, documentId]
    setFormData({ ...formData, knowledge_base_file_ids: nextIds })
  }

  const handleSelectAllKBDocs = () => {
    const allDocIds = filteredKbDocuments.map(doc => doc.document_id)
    setFormData({ ...formData, knowledge_base_file_ids: allDocIds })
  }

  const handleDeselectAllKBDocs = () => {
    setFormData({ ...formData, knowledge_base_file_ids: [] })
  }

  const handlePreviewDocument = async (documentId: string) => {
    if (previewDocId === documentId) {
      // Toggle off if clicking the same document
      setPreviewDocId(null)
      setPreviewContent(null)
      return
    }

    try {
      setPreviewLoading(true)
      setPreviewDocId(documentId)
      setPreviewContent(null)
      const response = await getKBDocumentContent(documentId)
      setPreviewContent(response.content)
    } catch (err: any) {
      setKbError(err.message || 'Failed to load document content')
      setPreviewContent('Error loading document content.')
    } finally {
      setPreviewLoading(false)
    }
  }

  useEffect(() => {
    if (activeTab === 'training' && !kbLoaded && !kbLoading) {
      loadKBDocuments()
    }
  }, [activeTab, kbLoaded, kbLoading])

  const readyKbDocuments = kbDocuments.filter(doc => doc.status === 'ready')

  const filteredKbDocuments = readyKbDocuments.filter(doc => {
    if (!kbSearchQuery.trim()) return true
    const query = kbSearchQuery.toLowerCase()
    return (
      doc.name.toLowerCase().includes(query) ||
      doc.category?.toLowerCase().includes(query) ||
      doc.description?.toLowerCase().includes(query)
    )
  })

  const renderReadOnlyPrompt = (value?: string, placeholder = 'No content provided.') => (
    <pre className="whitespace-pre-wrap break-words text-sm text-gray-700 bg-gray-50 p-4 rounded-lg border border-gray-100">
      {value?.trim() ? value : placeholder}
    </pre>
  )

  if (loading && agents.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-500"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">AI Agents</h1>
          <p className="text-gray-500 mt-1">
            Create and manage custom agents for different customer inquiries
          </p>
        </div>
        <div className="flex gap-3">
          {agents.length === 0 && (
            <button
              onClick={handleCreateDefaults}
              disabled={saving}
              className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition-colors"
            >
              <Sparkles className="w-4 h-4" />
              Create Default Agents
            </button>
          )}
          <button
            onClick={handleCreateNew}
            className="flex items-center gap-2 px-4 py-2 bg-yellow-500 hover:bg-yellow-600 text-gray-900 font-medium rounded-lg transition-colors"
          >
            <Plus className="w-4 h-4" />
            New Agent
          </button>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2">
          <AlertCircle className="w-5 h-5" />
          {error}
          <button onClick={() => setError(null)} className="ml-auto">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Global Settings Section - Super Admin Only */}
      {isSuperAdmin && <div className="bg-white rounded-xl shadow-sm border border-gray-100">
        <button
          onClick={() => setShowGlobalSettings(!showGlobalSettings)}
          className="w-full p-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <Settings className="w-5 h-5 text-blue-600" />
            </div>
            <div className="text-left">
              <h2 className="font-semibold text-gray-900">Global Agent Settings</h2>
              <p className="text-sm text-gray-500">Configuration shared across all agents</p>
            </div>
          </div>
          <ChevronRight className={`w-5 h-5 text-gray-400 transition-transform ${showGlobalSettings ? 'rotate-90' : ''}`} />
        </button>

        {showGlobalSettings && (
          <div className="p-6 border-t border-gray-100 space-y-6">
            <div className="flex justify-between items-center pb-4 border-b border-gray-100">
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={expandAllSections}
                  className="text-xs text-blue-600 hover:text-blue-700 flex items-center gap-1"
                >
                  <Maximize2 className="w-3 h-3" />
                  Expand All
                </button>
                <button
                  type="button"
                  onClick={collapseAllSections}
                  className="text-xs text-gray-600 hover:text-gray-700 flex items-center gap-1"
                >
                  <Minimize2 className="w-3 h-3" />
                  Collapse All
                </button>
              </div>
              <div className="flex gap-2">
                {isEditingGlobal ? (
                  <>
                    <button
                      onClick={handleCancelGlobal}
                      className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSaveGlobal}
                      disabled={savingGlobal}
                      className="flex items-center gap-2 px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white font-medium rounded-lg transition-colors"
                    >
                      <Save className="w-4 h-4" />
                      {savingGlobal ? 'Saving...' : 'Save Global Settings'}
                    </button>
                  </>
                ) : (
                  <button
                    onClick={handleEditGlobal}
                    className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition-colors"
                  >
                    <Edit className="w-4 h-4" />
                    Edit
                  </button>
                )}
              </div>
            </div>

            <div className="space-y-2">
              {/* Model Configuration Accordion */}
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <button
                  type="button"
                  onClick={() => toggleSection('model_config')}
                  className="w-full px-4 py-3 bg-blue-50 hover:bg-blue-100 flex items-center justify-between transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-gray-900">Model Configuration</span>
                    {!expandedSections.model_config && (
                      <span className="text-xs text-blue-600 font-medium">
                        {getModelInfo(globalConfig.model || 'gpt-5-mini')?.name || globalConfig.model} • {(globalConfig.reasoning_effort || 'medium').charAt(0).toUpperCase() + (globalConfig.reasoning_effort || 'medium').slice(1)} reasoning
                      </span>
                    )}
                  </div>
                  <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform ${expandedSections.model_config ? 'rotate-180' : ''}`} />
                </button>
                {expandedSections.model_config && (
                  <div className="p-4 bg-white space-y-5">
                    {/* Provider Selection */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-3">
                        AI Provider
                      </label>
                      <div className="grid grid-cols-4 gap-2">
                        {AI_PROVIDERS.map((provider) => {
                          const styles = PROVIDER_STYLES[provider.id]
                          const currentModel = isEditingGlobal ? globalFormData.model : globalConfig.model
                          const isSelected = getProviderFromModel(currentModel || 'gpt-5-mini') === provider.id
                          return (
                            <button
                              key={provider.id}
                              type="button"
                              disabled={!isEditingGlobal}
                              onClick={() => {
                                if (isEditingGlobal) {
                                  const defaultModel = provider.models.find(m => m.recommended)?.id || provider.models[0].id
                                  const newReasoning = getDefaultReasoningForProvider(provider.id, globalFormData.reasoning_effort)
                                  setGlobalFormData({ ...globalFormData, model: defaultModel, reasoning_effort: newReasoning })
                                }
                              }}
                              className={`relative px-3 py-2.5 rounded-lg border-2 text-center transition-all ${
                                isSelected
                                  ? styles.pillActive + ' ' + styles.pillActiveText
                                  : styles.pill + ' ' + styles.pillText
                              } ${!isEditingGlobal ? 'cursor-default opacity-75' : 'cursor-pointer'}`}
                            >
                              <div className="flex items-center justify-center gap-1.5">
                                <span className={`w-2 h-2 rounded-full ${isSelected ? styles.dot : 'bg-gray-300'}`} />
                                <span className="text-sm">{provider.name}</span>
                              </div>
                              {!provider.available && (
                                <span className="block text-xs mt-0.5 opacity-60">Coming Soon</span>
                              )}
                            </button>
                          )
                        })}
                      </div>
                    </div>

                    {/* Model Selection */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-3">
                        Model
                      </label>
                      {(() => {
                        const currentModel = isEditingGlobal ? globalFormData.model : globalConfig.model
                        const currentProvider = getProviderInfo(getProviderFromModel(currentModel || 'gpt-5-mini'))
                        const styles = PROVIDER_STYLES[currentProvider?.id || 'openai']
                        return (
                          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                            {currentProvider?.models.map((model) => {
                              const isSelected = (currentModel || 'gpt-5-mini') === model.id
                              return (
                                <button
                                  key={model.id}
                                  type="button"
                                  disabled={!isEditingGlobal}
                                  onClick={() => {
                                    if (isEditingGlobal) {
                                      setGlobalFormData({ ...globalFormData, model: model.id })
                                    }
                                  }}
                                  className={`relative px-3 py-3 rounded-lg border-2 text-left transition-all ${
                                    isSelected
                                      ? styles.ring + ' bg-gray-50 shadow-sm'
                                      : 'border-gray-200 bg-white hover:bg-gray-50'
                                  } ${!isEditingGlobal ? 'cursor-default opacity-75' : 'cursor-pointer'}`}
                                >
                                  <div className="flex items-start justify-between">
                                    <div>
                                      <p className={`text-sm font-medium ${isSelected ? 'text-gray-900' : 'text-gray-700'}`}>
                                        {model.name}
                                      </p>
                                      <p className="text-xs text-gray-500 mt-0.5">{model.description}</p>
                                    </div>
                                    {model.recommended && (
                                      <span className={`text-xs font-medium px-1.5 py-0.5 rounded-full ${styles.badge}`}>
                                        Top
                                      </span>
                                    )}
                                  </div>
                                  {isSelected && (
                                    <div className={`absolute top-1.5 right-1.5 w-2 h-2 rounded-full ${styles.dot}`} />
                                  )}
                                </button>
                              )
                            })}
                          </div>
                        )
                      })()}
                      {!getProviderInfo(getProviderFromModel((isEditingGlobal ? globalFormData.model : globalConfig.model) || 'gpt-5-mini'))?.available && (
                        <p className="mt-2 text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                          This provider is not yet available. Selection will be saved for when it becomes active.
                        </p>
                      )}
                    </div>

                    {/* Reasoning Level */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-3">
                        Reasoning Level
                      </label>
                      {(() => {
                        const currentModel = isEditingGlobal ? globalFormData.model : globalConfig.model
                        const providerId = getProviderFromModel(currentModel || 'gpt-5-mini')
                        const levels = getReasoningLevelsForProvider(providerId)
                        return (
                          <div className={`grid gap-2 ${levels.length === 2 ? 'grid-cols-2' : 'grid-cols-3'}`}>
                            {levels.map((level) => {
                              const currentEffort = isEditingGlobal
                                ? (globalFormData.reasoning_effort || 'medium')
                                : (globalConfig.reasoning_effort || 'medium')
                              const isSelected = currentEffort === level.id
                              const styles = REASONING_STYLES[level.id]
                              return (
                                <button
                                  key={level.id}
                                  type="button"
                                  disabled={!isEditingGlobal}
                                  onClick={() => {
                                    if (isEditingGlobal) {
                                      setGlobalFormData({ ...globalFormData, reasoning_effort: level.id })
                                    }
                                  }}
                                  className={`px-3 py-3 rounded-lg border-2 text-center transition-all ${
                                    isSelected
                                      ? styles.cardActive + ' ' + styles.textActive
                                      : styles.card + ' ' + styles.text
                                  } ${!isEditingGlobal ? 'cursor-default opacity-75' : 'cursor-pointer'}`}
                                >
                                  <p className="text-sm font-semibold">{level.name}</p>
                                  <p className="text-xs mt-0.5 opacity-75">{level.description}</p>
                                </button>
                              )
                            })}
                          </div>
                        )
                      })()}
                      <p className="mt-2 text-xs text-gray-500">
                        Higher reasoning provides better accuracy but increases response time and cost.
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Common Tone Accordion */}
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <button
                  type="button"
                  onClick={() => toggleSection('common_tone')}
                  className="w-full px-4 py-3 bg-gray-50 hover:bg-gray-100 flex items-center justify-between transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-gray-900">Common Tone</span>
                    {globalConfig.common_tone && !expandedSections.common_tone && (
                      <span className="text-xs text-gray-500">
                        ({globalConfig.common_tone.substring(0, 50)}...)
                      </span>
                    )}
                  </div>
                  <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform ${expandedSections.common_tone ? 'rotate-180' : ''}`} />
                </button>
                {expandedSections.common_tone && (
                  <div className="p-4 bg-white">
                    {isEditingGlobal ? (
                      <textarea
                        value={globalFormData.common_tone || ''}
                        onChange={(e) => setGlobalFormData({ ...globalFormData, common_tone: e.target.value })}
                        placeholder="Brand voice and tone shared by all agents..."
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        rows={6}
                      />
                    ) : (
                      renderReadOnlyPrompt(globalConfig.common_tone)
                    )}
                  </div>
                )}
              </div>

              {/* Escalation Protocol Accordion */}
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <button
                  type="button"
                  onClick={() => toggleSection('escalation_protocol')}
                  className="w-full px-4 py-3 bg-gray-50 hover:bg-gray-100 flex items-center justify-between transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-gray-900">Escalation Protocol</span>
                    {globalConfig.escalation_protocol && !expandedSections.escalation_protocol && (
                      <span className="text-xs text-gray-500">
                        ({globalConfig.escalation_protocol.substring(0, 50)}...)
                      </span>
                    )}
                  </div>
                  <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform ${expandedSections.escalation_protocol ? 'rotate-180' : ''}`} />
                </button>
                {expandedSections.escalation_protocol && (
                  <div className="p-4 bg-white">
                    {isEditingGlobal ? (
                      <textarea
                        value={globalFormData.escalation_protocol || ''}
                        onChange={(e) => setGlobalFormData({ ...globalFormData, escalation_protocol: e.target.value })}
                        placeholder="Escalation levels and trigger rules..."
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        rows={6}
                      />
                    ) : (
                      renderReadOnlyPrompt(globalConfig.escalation_protocol)
                    )}
                  </div>
                )}
              </div>

              {/* Compensation Limits Accordion */}
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <button
                  type="button"
                  onClick={() => toggleSection('compensation_limits')}
                  className="w-full px-4 py-3 bg-gray-50 hover:bg-gray-100 flex items-center justify-between transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-gray-900">Compensation Limits</span>
                    {globalConfig.compensation_limits && !expandedSections.compensation_limits && (
                      <span className="text-xs text-gray-500">
                        ({globalConfig.compensation_limits.substring(0, 50)}...)
                      </span>
                    )}
                  </div>
                  <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform ${expandedSections.compensation_limits ? 'rotate-180' : ''}`} />
                </button>
                {expandedSections.compensation_limits && (
                  <div className="p-4 bg-white">
                    {isEditingGlobal ? (
                      <textarea
                        value={globalFormData.compensation_limits || ''}
                        onChange={(e) => setGlobalFormData({ ...globalFormData, compensation_limits: e.target.value })}
                        placeholder="What can be compensated automatically vs escalated..."
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        rows={6}
                      />
                    ) : (
                      renderReadOnlyPrompt(globalConfig.compensation_limits)
                    )}
                  </div>
                )}
              </div>

              {/* Corporate Guardrails Accordion */}
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <button
                  type="button"
                  onClick={() => toggleSection('corporate_guardrails')}
                  className="w-full px-4 py-3 bg-gray-50 hover:bg-gray-100 flex items-center justify-between transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-gray-900">Corporate Guardrails</span>
                    {globalConfig.corporate_guardrails && !expandedSections.corporate_guardrails && (
                      <span className="text-xs text-gray-500">
                        ({globalConfig.corporate_guardrails.substring(0, 50)}...)
                      </span>
                    )}
                  </div>
                  <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform ${expandedSections.corporate_guardrails ? 'rotate-180' : ''}`} />
                </button>
                {expandedSections.corporate_guardrails && (
                  <div className="p-4 bg-white">
                    {isEditingGlobal ? (
                      <textarea
                        value={globalFormData.corporate_guardrails || ''}
                        onChange={(e) => setGlobalFormData({ ...globalFormData, corporate_guardrails: e.target.value })}
                        placeholder="Information that must never be shared..."
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        rows={6}
                      />
                    ) : (
                      renderReadOnlyPrompt(globalConfig.corporate_guardrails)
                    )}
                  </div>
                )}
              </div>

              {/* Store Data Accordion */}
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <button
                  type="button"
                  onClick={() => toggleSection('store_data')}
                  className="w-full px-4 py-3 bg-gray-50 hover:bg-gray-100 flex items-center justify-between transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-gray-900">Store Data</span>
                    {globalConfig.store_data && !expandedSections.store_data && (
                      <span className="text-xs text-gray-500">
                        ({globalConfig.store_data.substring(0, 50)}...)
                      </span>
                    )}
                  </div>
                  <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform ${expandedSections.store_data ? 'rotate-180' : ''}`} />
                </button>
                {expandedSections.store_data && (
                  <div className="p-4 bg-white">
                    {isEditingGlobal ? (
                      <textarea
                        value={globalFormData.store_data || ''}
                        onChange={(e) => setGlobalFormData({ ...globalFormData, store_data: e.target.value })}
                        placeholder="Store metadata, pricing, policies, or other shared facts..."
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        rows={6}
                      />
                    ) : (
                      renderReadOnlyPrompt(globalConfig.store_data)
                    )}
                  </div>
                )}
              </div>

              {/* Context Instructions Accordion */}
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <button
                  type="button"
                  onClick={() => toggleSection('context_instructions')}
                  className="w-full px-4 py-3 bg-gray-50 hover:bg-gray-100 flex items-center justify-between transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-gray-900">Context</span>
                    {globalConfig.context_instructions && !expandedSections.context_instructions && (
                      <span className="text-xs text-gray-500">
                        ({globalConfig.context_instructions.substring(0, 50)}...)
                      </span>
                    )}
                  </div>
                  <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform ${expandedSections.context_instructions ? 'rotate-180' : ''}`} />
                </button>
                {expandedSections.context_instructions && (
                  <div className="p-4 bg-white">
                    {isEditingGlobal ? (
                      <textarea
                        value={globalFormData.context_instructions || ''}
                        onChange={(e) => setGlobalFormData({ ...globalFormData, context_instructions: e.target.value })}
                        placeholder="Context available to agents (customer info, channel hints, order notes)..."
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        rows={6}
                      />
                    ) : (
                      renderReadOnlyPrompt(globalConfig.context_instructions)
                    )}
                  </div>
                )}
              </div>

              {/* Special Instructions Accordion */}
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <button
                  type="button"
                  onClick={() => toggleSection('special_instructions')}
                  className="w-full px-4 py-3 bg-gray-50 hover:bg-gray-100 flex items-center justify-between transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-gray-900">Special Instructions</span>
                    {globalConfig.special_instructions && !expandedSections.special_instructions && (
                      <span className="text-xs text-gray-500">
                        ({globalConfig.special_instructions.substring(0, 50)}...)
                      </span>
                    )}
                  </div>
                  <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform ${expandedSections.special_instructions ? 'rotate-180' : ''}`} />
                </button>
                {expandedSections.special_instructions && (
                  <div className="p-4 bg-white">
                    {isEditingGlobal ? (
                      <textarea
                        value={globalFormData.special_instructions || ''}
                        onChange={(e) => setGlobalFormData({ ...globalFormData, special_instructions: e.target.value })}
                        placeholder="Escalation rules, sensitive handling, or exceptions..."
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        rows={6}
                      />
                    ) : (
                      renderReadOnlyPrompt(globalConfig.special_instructions)
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>}

      <div className="flex flex-col lg:flex-row gap-6">
        {/* Agent List */}
        <div className="lg:w-80 flex-shrink-0">
          <div className="bg-white rounded-xl shadow-sm border border-gray-100">
            <div className="p-4 border-b border-gray-100">
              <h2 className="font-semibold text-gray-900">Your Agents</h2>
              <p className="text-sm text-gray-500 mt-1">{agents.length} agents configured</p>
            </div>
            <div className="divide-y divide-gray-100 max-h-[600px] overflow-y-auto">
              {agents.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  <Bot className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>No agents yet</p>
                  <p className="text-sm mt-1">Create your first agent or use defaults</p>
                </div>
              ) : (
                agents.map((agent) => (
                  <button
                    key={agent.agent_id}
                    onClick={() => handleSelectAgent(agent)}
                    className={`w-full p-4 text-left hover:bg-gray-50 transition-colors flex items-center gap-3 ${
                      selectedAgent?.agent_id === agent.agent_id ? 'bg-yellow-50' : ''
                    }`}
                  >
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      agent.is_active ? 'bg-green-100' : 'bg-gray-100'
                    }`}>
                      <Bot className={`w-5 h-5 ${agent.is_active ? 'text-green-600' : 'text-gray-400'}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-900 truncate">{agent.name}</p>
                      <p className="text-sm text-gray-500 truncate">
                        {agent.categories.length} categories
                      </p>
                    </div>
                    <ChevronRight className="w-5 h-5 text-gray-400" />
                  </button>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Agent Details / Editor */}
        <div className="flex-1">
          {selectedAgent || isCreating ? (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100">
              {/* Agent Header */}
              <div className="p-6 border-b border-gray-100 flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-yellow-100 rounded-xl flex items-center justify-center">
                    <Bot className="w-6 h-6 text-yellow-600" />
                  </div>
                  <div>
                    {isEditing ? (
                      <input
                        type="text"
                        value={formData.name || ''}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        placeholder="Agent Name"
                        className="text-xl font-bold text-gray-900 border-b border-gray-300 focus:border-yellow-500 outline-none pb-1"
                      />
                    ) : (
                      <h2 className="text-xl font-bold text-gray-900">
                        {selectedAgent?.name || 'New Agent'}
                      </h2>
                    )}
                    <p className="text-gray-500 text-sm">
                      {isCreating ? 'Creating new agent' : `Version ${selectedAgent?.version || 1}`}
                    </p>
                  </div>
                </div>
                <div className="flex gap-2">
                  {isEditing ? (
                    <>
                      <button
                        onClick={handleCancel}
                        className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg"
                      >
                        <X className="w-5 h-5" />
                      </button>
                      <button
                        onClick={handleSave}
                        disabled={saving}
                        className="flex items-center gap-2 px-4 py-2 bg-yellow-500 hover:bg-yellow-600 text-gray-900 font-medium rounded-lg"
                      >
                        <Save className="w-4 h-4" />
                        {saving ? 'Saving...' : 'Save'}
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        onClick={handleEdit}
                        className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg"
                      >
                        <Edit className="w-5 h-5" />
                      </button>
                      <button
                        onClick={handleDelete}
                        className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </>
                  )}
                </div>
              </div>

              {/* Tabs */}
              <div className="border-b border-gray-100">
                <AgentTabs activeTab={activeTab} onTabChange={setActiveTab} />
              </div>

              {/* Content */}
              <div className="p-6 space-y-8">
                {activeTab === 'configuration' && (
                  <>
                    {/* Description */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Description
                      </label>
                      {isEditing ? (
                        <textarea
                          value={formData.description || ''}
                          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                          placeholder="What does this agent do?"
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                          rows={2}
                        />
                      ) : (
                        <p className="text-gray-600">{selectedAgent?.description || 'No description'}</p>
                      )}
                    </div>

                    {/* Global Settings Toggle - Super Admin Only */}
                    {isSuperAdmin && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <Settings className="w-5 h-5 text-blue-600" />
                          <div>
                            <label className="block text-sm font-medium text-gray-900">
                              Apply Global Agent Settings
                            </label>
                            <p className="text-xs text-gray-600 mt-0.5">
                              When enabled, this agent will use the Global Agent Settings (Common Tone, Escalation Protocol, etc.) in addition to its own configuration
                            </p>
                          </div>
                        </div>
                        {isEditing ? (
                          <label className="relative inline-flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              checked={formData.use_global_settings !== false}
                              onChange={(e) => setFormData({ ...formData, use_global_settings: e.target.checked })}
                              className="sr-only peer"
                            />
                            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                          </label>
                        ) : (
                          <div className={`px-3 py-1 rounded-full text-xs font-medium ${
                            (selectedAgent?.use_global_settings !== false) ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                          }`}>
                            {(selectedAgent?.use_global_settings !== false) ? 'Enabled' : 'Disabled'}
                          </div>
                        )}
                      </div>
                    </div>
                    )}

                    {/* AI Router Toggle - Super Admin Only */}
                    {isSuperAdmin && (
                    <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <Bot className="w-5 h-5 text-purple-600" />
                          <div>
                            <label className="block text-sm font-medium text-gray-900">
                              AI Router (Classification)
                            </label>
                            <p className="text-xs text-gray-600 mt-0.5">
                              When enabled, AI Router will use this agent to classify incoming emails
                            </p>
                          </div>
                        </div>
                        {isEditing ? (
                          <label className="relative inline-flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              checked={formData.router_enabled !== false}
                              onChange={(e) => setFormData({ ...formData, router_enabled: e.target.checked })}
                              className="sr-only peer"
                            />
                            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
                          </label>
                        ) : (
                          <div className={`px-3 py-1 rounded-full text-xs font-medium ${
                            (selectedAgent?.router_enabled !== false) ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                          }`}>
                            {(selectedAgent?.router_enabled !== false) ? 'Enabled' : 'Disabled'}
                          </div>
                        )}
                      </div>
                    </div>
                    )}

                    {/* AI Draft Response Toggle */}
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <Sparkles className="w-5 h-5 text-green-600" />
                          <div>
                            <label className="block text-sm font-medium text-gray-900">
                              AI Draft Response
                            </label>
                            <p className="text-xs text-gray-600 mt-0.5">
                              When enabled, this agent can generate draft responses for classified emails
                            </p>
                          </div>
                        </div>
                        {isEditing ? (
                          <label className="relative inline-flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              checked={formData.draft_response_enabled !== false}
                              onChange={(e) => setFormData({ ...formData, draft_response_enabled: e.target.checked })}
                              className="sr-only peer"
                            />
                            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600"></div>
                          </label>
                        ) : (
                          <div className={`px-3 py-1 rounded-full text-xs font-medium ${
                            (selectedAgent?.draft_response_enabled !== false) ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                          }`}>
                            {(selectedAgent?.draft_response_enabled !== false) ? 'Enabled' : 'Disabled'}
                          </div>
                        )}
                      </div>

                      {/* Response Mode Selector - shown when draft response is enabled */}
                      {(isEditing ? formData.draft_response_enabled !== false : selectedAgent?.draft_response_enabled !== false) && (
                        <div className="mt-4 pt-4 border-t border-green-200">
                          <label className="block text-xs font-medium text-gray-700 mb-2">Response Mode</label>
                          {isEditing ? (
                            <div className="grid grid-cols-2 gap-2">
                              {[
                                { value: 'auto_reply', label: 'Auto Reply', desc: 'AI generates response using agent config & prompts', icon: '🤖' },
                                { value: 'manual', label: 'Manual', desc: 'Human writes and sends the response', icon: '✍️' },
                              ].map((mode) => (
                                <button
                                  key={mode.value}
                                  type="button"
                                  onClick={() => setFormData({ ...formData, response_mode: mode.value })}
                                  className={`p-3 rounded-lg border-2 text-left transition-all ${
                                    (formData.response_mode || 'auto_reply') === mode.value
                                      ? 'border-green-500 bg-green-100 shadow-sm'
                                      : 'border-gray-200 bg-white hover:border-gray-300'
                                  }`}
                                >
                                  <div className="text-lg mb-1">{mode.icon}</div>
                                  <div className="text-xs font-semibold text-gray-900">{mode.label}</div>
                                  <div className="text-xs text-gray-500 mt-0.5">{mode.desc}</div>
                                </button>
                              ))}
                            </div>
                          ) : (
                            <div className="flex items-center gap-2">
                              <span className="text-lg">
                                {(selectedAgent?.response_mode || 'auto_reply') === 'auto_reply' ? '🤖' : '✍️'}
                              </span>
                              <span className="text-sm text-gray-700 font-medium">
                                {(selectedAgent?.response_mode || 'auto_reply') === 'auto_reply' ? 'Auto Reply' : 'Manual'}
                              </span>
                              <span className="text-xs text-gray-500">
                                {(selectedAgent?.response_mode || 'auto_reply') === 'auto_reply' ? '— AI generates response using agent config & prompts' : '— Human writes and sends the response'}
                              </span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Agent-Specific Configuration */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-3">
                        <Sparkles className="w-4 h-4 inline mr-2" />
                        Agent-Specific Configuration
                      </label>
                      <p className="text-sm text-gray-500 mb-4">
                        Configure settings unique to this agent. Define the agent's specific role, behavior, and instructions that differentiate it from other agents in your system.
                      </p>
                      <div className="space-y-4">
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">Agent Prompt</label>
                          {isEditing ? (
                            <textarea
                              value={formData.agent_prompt || ''}
                              onChange={(e) => setFormData({ ...formData, agent_prompt: e.target.value })}
                              placeholder="Agent-specific instructions and role..."
                              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                              rows={4}
                            />
                          ) : (
                            renderReadOnlyPrompt(formData.agent_prompt)
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Response Templates - Collapsible Scenarios */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        <FileText className="w-4 h-4 inline mr-2" />
                        Response Templates
                      </label>
                      <p className="text-sm text-gray-500 mb-4">
                        Create templates this agent can use when generating draft responses. Define real scenarios your team handles frequently so the AI knows exactly how to respond.
                      </p>

                      {/* Controls */}
                      {(isEditing ? (formData.response_templates || []) : (selectedAgent?.response_templates || [])).length > 0 && (
                        <div className="flex justify-between items-center mb-3">
                          <div className="flex gap-2">
                            <button
                              type="button"
                              onClick={() => {
                                const templates = isEditing ? (formData.response_templates || []) : (selectedAgent?.response_templates || [])
                                const all: Record<string, boolean> = {}
                                templates.forEach(t => { all[t.template_id] = true })
                                setExpandedScenarios(all)
                              }}
                              className="text-xs text-blue-600 hover:text-blue-700 flex items-center gap-1"
                            >
                              <Maximize2 className="w-3 h-3" />
                              Expand All
                            </button>
                            <button
                              type="button"
                              onClick={() => setExpandedScenarios({})}
                              className="text-xs text-gray-600 hover:text-gray-700 flex items-center gap-1"
                            >
                              <Minimize2 className="w-3 h-3" />
                              Collapse All
                            </button>
                          </div>
                          {isEditing && (
                            <button
                              type="button"
                              onClick={() => {
                                const templates = formData.response_templates || []
                                const newTemplate: ResponseTemplate = {
                                  template_id: `tmpl_${Date.now()}`,
                                  name: `Scenario ${templates.length + 1}: `,
                                  content: '',
                                  category: 'scenario',
                                  language: 'NO',
                                  description: '',
                                  is_active: true,
                                }
                                setFormData({ ...formData, response_templates: [...templates, newTemplate] })
                                setExpandedScenarios(prev => ({ ...prev, [newTemplate.template_id]: true }))
                              }}
                              className="flex items-center gap-1 px-3 py-1.5 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 text-xs font-medium"
                            >
                              <Plus className="w-3 h-3" />
                              Add Scenario
                            </button>
                          )}
                        </div>
                      )}

                      {/* Scenario List */}
                      {(isEditing ? (formData.response_templates || []) : (selectedAgent?.response_templates || [])).length === 0 ? (
                        <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center">
                          <FileText className="w-10 h-10 mx-auto mb-2 text-gray-300" />
                          <p className="text-sm text-gray-500 mb-1">No scenarios yet</p>
                          <p className="text-xs text-gray-400 mb-3">
                            Add real cases your team handles to help the AI draft better responses.
                          </p>
                          {isEditing && (
                            <button
                              type="button"
                              onClick={() => {
                                const templates = formData.response_templates || []
                                const newTemplate: ResponseTemplate = {
                                  template_id: `tmpl_${Date.now()}`,
                                  name: `Scenario 1: `,
                                  content: '',
                                  category: 'scenario',
                                  language: 'NO',
                                  description: '',
                                  is_active: true,
                                }
                                setFormData({ ...formData, response_templates: [...templates, newTemplate] })
                                setExpandedScenarios(prev => ({ ...prev, [newTemplate.template_id]: true }))
                              }}
                              className="inline-flex items-center gap-1 px-3 py-1.5 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 text-xs font-medium"
                            >
                              <Plus className="w-3 h-3" />
                              Add Scenario
                            </button>
                          )}
                        </div>
                      ) : (
                        <div className="space-y-2">
                          {(isEditing ? (formData.response_templates || []) : (selectedAgent?.response_templates || [])).map((template, idx) => (
                            <div
                              key={template.template_id}
                              className="border border-gray-200 rounded-lg overflow-hidden"
                            >
                              <button
                                type="button"
                                onClick={() => setExpandedScenarios(prev => ({ ...prev, [template.template_id]: !prev[template.template_id] }))}
                                className="w-full px-4 py-3 bg-gray-50 hover:bg-gray-100 flex items-center justify-between transition-colors"
                              >
                                <div className="flex items-center gap-2 min-w-0">
                                  <span className="font-medium text-gray-900 truncate">
                                    {template.name || `Scenario ${idx + 1}`}
                                  </span>
                                  {!expandedScenarios[template.template_id] && template.content && (
                                    <span className="text-xs text-gray-400 truncate hidden sm:inline">
                                      ({template.content.slice(0, 50)}{template.content.length > 50 ? '...' : ''})
                                    </span>
                                  )}
                                </div>
                                <div className="flex items-center gap-2 flex-shrink-0">
                                  {template.is_active === false && (
                                    <span className="px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-500">Inactive</span>
                                  )}
                                  {isEditing && (
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.stopPropagation()
                                        const templates = (formData.response_templates || []).filter((_, i) => i !== idx)
                                        setFormData({ ...formData, response_templates: templates })
                                      }}
                                      className="p-1 text-red-400 hover:text-red-600 hover:bg-red-50 rounded"
                                      title="Delete scenario"
                                    >
                                      <Trash2 className="w-3.5 h-3.5" />
                                    </button>
                                  )}
                                  <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform ${expandedScenarios[template.template_id] ? 'rotate-180' : ''}`} />
                                </div>
                              </button>
                              {expandedScenarios[template.template_id] && (
                                <div className="p-4 bg-white space-y-3 border-t border-gray-200">
                                  <div>
                                    <label className="block text-xs text-gray-500 mb-1">Scenario Title</label>
                                    {isEditing ? (
                                      <input
                                        type="text"
                                        value={template.name}
                                        onChange={(e) => {
                                          const templates = [...(formData.response_templates || [])]
                                          templates[idx] = { ...templates[idx], name: e.target.value }
                                          setFormData({ ...formData, response_templates: templates })
                                        }}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                                        placeholder="e.g., Scenario 1: If the client requests delivery time"
                                      />
                                    ) : (
                                      <p className="text-sm text-gray-700 font-medium">{template.name}</p>
                                    )}
                                  </div>
                                  <div>
                                    <label className="block text-xs text-gray-500 mb-1">Predefined Response</label>
                                    {isEditing ? (
                                      <textarea
                                        value={template.content || ''}
                                        onChange={(e) => {
                                          const templates = [...(formData.response_templates || [])]
                                          templates[idx] = { ...templates[idx], content: e.target.value }
                                          setFormData({ ...formData, response_templates: templates })
                                        }}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                                        rows={4}
                                        placeholder="Write the predefined response for this scenario..."
                                      />
                                    ) : (
                                      <pre className="whitespace-pre-wrap text-sm text-gray-700 bg-gray-50 rounded-lg p-3 border border-gray-200 font-mono max-h-48 overflow-y-auto">
                                        {template.content || 'No response defined'}
                                      </pre>
                                    )}
                                  </div>
                                  {isEditing && (
                                    <div className="flex items-center gap-2 pt-1">
                                      <label className="text-xs text-gray-500">Active</label>
                                      <label className="relative inline-flex items-center cursor-pointer">
                                        <input
                                          type="checkbox"
                                          checked={template.is_active !== false}
                                          onChange={(e) => {
                                            const templates = [...(formData.response_templates || [])]
                                            templates[idx] = { ...templates[idx], is_active: e.target.checked }
                                            setFormData({ ...formData, response_templates: templates })
                                          }}
                                          className="sr-only peer"
                                        />
                                        <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-green-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-green-500"></div>
                                      </label>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                  </>
                )}

                {activeTab === 'training' && (
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-sm text-gray-600">
                    <div className="space-y-4 text-gray-700">
                        <div>
                          <h3 className="text-sm font-semibold text-gray-900">Knowledge Base Sources</h3>
                          <p className="text-sm text-gray-600 mt-1">
                            Link this agent to the knowledge base files it should reference when answering emails.
                          </p>
                        </div>
                        {kbError && (
                          <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                            {kbError}
                          </div>
                        )}
                        <div>
                          <div className="flex items-center justify-between mb-2">
                            <label className="block text-xs text-gray-500">Knowledge Base Documents</label>
                            {isEditing && readyKbDocuments.length > 0 && (
                              <div className="flex gap-2">
                                <button
                                  type="button"
                                  onClick={handleSelectAllKBDocs}
                                  className="text-xs text-blue-600 hover:text-blue-700 flex items-center gap-1"
                                >
                                  <CheckSquare className="w-3 h-3" />
                                  Select All
                                </button>
                                <button
                                  type="button"
                                  onClick={handleDeselectAllKBDocs}
                                  className="text-xs text-gray-600 hover:text-gray-700 flex items-center gap-1"
                                >
                                  <Square className="w-3 h-3" />
                                  Deselect All
                                </button>
                              </div>
                            )}
                          </div>

                          {/* Search Bar */}
                          {readyKbDocuments.length > 0 && (
                            <div className="mb-3">
                              <div className="relative">
                                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                                <input
                                  type="text"
                                  placeholder="Search documents by name, category..."
                                  value={kbSearchQuery}
                                  onChange={(e) => setKbSearchQuery(e.target.value)}
                                  className="w-full pl-10 pr-4 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                />
                                {kbSearchQuery && (
                                  <button
                                    type="button"
                                    onClick={() => setKbSearchQuery('')}
                                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                  >
                                    <X className="w-4 h-4" />
                                  </button>
                                )}
                              </div>
                            </div>
                          )}

                          <div className="border border-gray-200 rounded-lg bg-white">
                            {kbLoading ? (
                              <div className="p-8 flex flex-col items-center justify-center space-y-4">
                                <div className="relative">
                                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
                                  <div className="absolute inset-0 flex items-center justify-center">
                                    <FileText className="w-5 h-5 text-blue-500" />
                                  </div>
                                </div>
                                <div className="text-center">
                                  <p className="text-sm font-medium text-gray-700">Loading Knowledge Base</p>
                                  <p className="text-xs text-gray-500 mt-1">Fetching your documents...</p>
                                </div>
                                <div className="flex gap-1">
                                  <div className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                                  <div className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                                  <div className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                                </div>
                              </div>
                            ) : readyKbDocuments.length === 0 ? (
                              <div className="p-4 text-sm text-gray-500">
                                No ready documents found. Upload documents in the Knowledge Base first.
                              </div>
                            ) : filteredKbDocuments.length === 0 ? (
                              <div className="p-4 text-sm text-gray-500">
                                No documents match your search. Try a different query.
                              </div>
                            ) : (
                              <div className="max-h-64 overflow-y-auto divide-y divide-gray-100">
                                {filteredKbDocuments.map((doc) => {
                                  const isSelected = (formData.knowledge_base_file_ids || []).includes(doc.document_id)
                                  const isPreviewing = previewDocId === doc.document_id
                                  return (
                                    <div
                                      key={doc.document_id}
                                      className={`flex items-start gap-3 px-4 py-3 text-sm transition-colors ${
                                        isPreviewing ? 'bg-blue-50' : isEditing ? 'hover:bg-gray-50' : ''
                                      }`}
                                    >
                                      <input
                                        type="checkbox"
                                        className="mt-1 accent-yellow-500 cursor-pointer flex-shrink-0"
                                        checked={isSelected}
                                        disabled={!isEditing}
                                        onChange={() => handleToggleKnowledgeBaseDoc(doc.document_id)}
                                      />
                                      <div
                                        className={`flex-1 min-w-0 ${isEditing ? 'cursor-pointer' : ''}`}
                                        onClick={() => isEditing && handleToggleKnowledgeBaseDoc(doc.document_id)}
                                      >
                                        <div className="font-medium text-gray-900">{doc.name}</div>
                                        <div className="text-xs text-gray-500">
                                          {doc.category || 'Uncategorized'} • {doc.file_type?.toUpperCase() || 'FILE'}
                                        </div>
                                      </div>
                                      <button
                                        type="button"
                                        onClick={(e) => {
                                          e.stopPropagation()
                                          handlePreviewDocument(doc.document_id)
                                        }}
                                        className={`flex items-center gap-1 px-2 py-1 text-xs rounded transition-colors flex-shrink-0 ${
                                          isPreviewing
                                            ? 'bg-blue-100 text-blue-700'
                                            : 'text-gray-600 hover:bg-gray-100'
                                        }`}
                                        title="Preview document content"
                                      >
                                        <Eye className="w-3 h-3" />
                                        {isPreviewing ? 'Hide' : 'View'}
                                      </button>
                                    </div>
                                  )
                                })}
                              </div>
                            )}
                          </div>
                          {isEditing && (
                            <div className="mt-2 text-xs text-gray-500">
                              Selected {formData.knowledge_base_file_ids?.length || 0} of {readyKbDocuments.length} documents.
                            </div>
                          )}
                          {!isEditing && (formData.knowledge_base_file_ids || []).length === 0 && (
                            <div className="mt-2 text-xs text-gray-500">
                              No knowledge base documents linked.
                            </div>
                          )}

                          {/* Document Preview */}
                          {previewDocId && (
                            <div className="mt-4 border border-gray-200 rounded-lg bg-gray-50">
                              <div className="px-4 py-2 bg-gray-100 border-b border-gray-200 flex items-center justify-between rounded-t-lg">
                                <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
                                  <FileText className="w-4 h-4" />
                                  Document Preview
                                </div>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setPreviewDocId(null)
                                    setPreviewContent(null)
                                  }}
                                  className="text-gray-500 hover:text-gray-700"
                                >
                                  <X className="w-4 h-4" />
                                </button>
                              </div>
                              <div className="p-4 max-h-96 overflow-y-auto">
                                {previewLoading ? (
                                  <div className="flex items-center justify-center py-8">
                                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
                                  </div>
                                ) : previewContent ? (
                                  <pre className="whitespace-pre-wrap text-xs text-gray-700 font-mono">
                                    {previewContent}
                                  </pre>
                                ) : (
                                  <div className="text-sm text-gray-500 text-center py-4">
                                    No content available
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                        {isEditing && (
                          <div className="text-xs text-gray-500">
                            Need to add more sources? Upload them in the Knowledge Base section.
                          </div>
                        )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-12 text-center">
              <Bot className="w-16 h-16 mx-auto mb-4 text-gray-300" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Select an Agent</h3>
              <p className="text-gray-500 mb-6">
                Choose an agent from the list to view or edit its configuration
              </p>
              <button
                onClick={handleCreateNew}
                className="inline-flex items-center gap-2 px-4 py-2 bg-yellow-500 hover:bg-yellow-600 text-gray-900 font-medium rounded-lg"
              >
                <Plus className="w-4 h-4" />
                Create New Agent
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// Simple tabs component
function AgentTabs({
  activeTab,
  onTabChange,
}: {
  activeTab: 'configuration' | 'training'
  onTabChange: (tab: 'configuration' | 'training') => void
}) {
  return (
    <div className="flex gap-6 px-6">
      <button
        onClick={() => onTabChange('configuration')}
        className={`py-3 font-medium ${
          activeTab === 'configuration'
            ? 'text-yellow-600 border-b-2 border-yellow-500'
            : 'text-gray-500 hover:text-gray-700'
        }`}
      >
        Configuration
      </button>
      <button
        onClick={() => onTabChange('training')}
        className={`py-3 font-medium ${
          activeTab === 'training'
            ? 'text-yellow-600 border-b-2 border-yellow-500'
            : 'text-gray-500 hover:text-gray-700'
        }`}
      >
        Training Data
      </button>
    </div>
  )
}
